import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/Card';
import { Button } from './ui/Button';
import { Shield, Eye, Lock, FileText, Users, Globe, Mail, Database, UserX, Cookie } from 'lucide-react';

const PrivacyPolicyPage = () => {
  const sections = [
    {
      id: 'information-we-collect',
      title: 'Information We Collect',
      icon: <Eye className="h-5 w-5" />,
      content: [
        {
          subtitle: 'What We Collect',
          text: 'We collect only the following information when you register or use our service: Email address – used to create and manage your account, track credit usage, and send service-related messages.'
        },
        {
          subtitle: 'What We Do NOT Collect',
          text: 'We do not collect or store: Your name, address, or payment details (these are handled by our third-party payment provider), Your input content or generated output, Any biometric or sensitive personal data.'
        }
      ]
    },
    {
      id: 'how-we-use-information',
      title: 'How We Use Your Information',
      icon: <FileText className="h-5 w-5" />,
      content: [
        {
          subtitle: 'Email Usage',
          text: 'We use your email address to: Create and manage your account, Track your credit balance, Send important service updates (e.g., credit usage, policy changes), Provide customer support when requested.'
        },
        {
          subtitle: 'What We Don\'t Do',
          text: 'We do not send marketing emails or sell your information.'
        }
      ]
    },
    {
      id: 'data-protection',
      title: 'How We Protect Your Data',
      icon: <Lock className="h-5 w-5" />,
      content: [
        {
          subtitle: 'Security Measures',
          text: 'We implement security measures to protect your data, including: Secure database storage, Encrypted connections (HTTPS), Limited internal access to data.'
        },
        {
          subtitle: 'Important Note',
          text: 'However, no online system is 100% secure. You use our service at your own risk.'
        }
      ]
    },
    {
      id: 'third-party-services',
      title: 'Third-Party Services',
      icon: <Users className="h-5 w-5" />,
      content: [
        {
          subtitle: 'External Services',
          text: 'We use third-party services for: Authentication, Payments. These services may collect information as needed to process your requests. We recommend reviewing their privacy policies as well.'
        }
      ]
    },
    {
      id: 'cookies',
      title: 'Cookies',
      icon: <Cookie className="h-5 w-5" />,
      content: [
        {
          subtitle: 'Cookie Usage',
          text: 'We may use cookies or local storage to: Maintain session login, Track usage for basic analytics (anonymous only).'
        },
        {
          subtitle: 'Cookie Control',
          text: 'You can disable cookies via your browser settings, but this may affect site functionality.'
        }
      ]
    },
    {
      id: 'data-retention',
      title: 'Data Retention',
      icon: <Database className="h-5 w-5" />,
      content: [
        {
          subtitle: 'Account Data',
          text: 'We retain your email and credit balance until you delete your account or request removal.'
        },
        {
          subtitle: 'Content Data',
          text: 'We do not retain your content submissions or generated results.'
        }
      ]
    },
    {
      id: 'your-rights',
      title: 'Your Rights',
      icon: <Shield className="h-5 w-5" />,
      content: [
        {
          subtitle: 'User Rights',
          text: 'Depending on your location, you may have the right to: Access the information we hold about you, Request deletion of your account and data, Request correction of inaccurate data.'
        },
        {
          subtitle: 'Contact for Rights',
          text: 'To exercise these rights, contact us at thataihumanizer@gmail.com.'
        }
      ]
    },
    {
      id: 'children-privacy',
      title: 'Children\'s Privacy',
      icon: <UserX className="h-5 w-5" />,
      content: [
        {
          subtitle: 'Age Restriction',
          text: 'ThatAIHumanizer is not intended for children under 13. We do not knowingly collect data from minors.'
        }
      ]
    },
    {
      id: 'data-location',
      title: 'Data Location and Jurisdiction',
      icon: <Globe className="h-5 w-5" />,
      content: [
        {
          subtitle: 'Service Location',
          text: 'Our service is operated from the Philippines, and your data may be stored or processed on servers located elsewhere. By using our service, you consent to this data transfer.'
        }
      ]
    },
    {
      id: 'policy-changes',
      title: 'Changes to This Policy',
      icon: <FileText className="h-5 w-5" />,
      content: [
        {
          subtitle: 'Updates',
          text: 'We may update this Privacy Policy from time to time. We\'ll notify you of significant changes via email or our website. Continued use after such updates implies acceptance.'
        }
      ]
    }
  ];

  const lastUpdated = "June 1, 2025";

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-primary/10 via-background to-secondary/10 py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="flex justify-center mb-6">
              <div className="p-4 bg-primary/10 rounded-full">
                <Shield className="h-12 w-12 text-primary" />
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6 text-foreground">
              Privacy Policy
            </h1>
            <p className="text-xl text-muted-foreground mb-8 leading-relaxed">
              This Privacy Policy describes how ThatAIHumanizer ("we," "us," or "our") collects, uses, and protects your personal information when you use our website https://thataihumanizer.pages.dev (the "Site") and related services.
            </p>
            <div className="flex justify-center gap-4">
              <Button variant="outline" asChild>
                <Link to="/">Back to Home</Link>
              </Button>
              <Button asChild>
                <Link to="/app">Try Our Service</Link>
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Table of Contents */}
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Table of Contents
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-2">
                {sections.map((section, index) => (
                  <a
                    key={section.id}
                    href={`#${section.id}`}
                    className="flex items-center gap-2 p-2 rounded-md hover:bg-muted/50 transition-colors text-sm text-muted-foreground hover:text-foreground"
                  >
                    {section.icon}
                    <span>{index + 1}. {section.title}</span>
                  </a>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Introduction */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle>Effective Date: {lastUpdated}</CardTitle>
            </CardHeader>
            <CardContent className="prose prose-sm max-w-none dark:prose-invert">
              <p className="text-muted-foreground leading-relaxed">
                This Privacy Policy describes how ThatAIHumanizer ("we," "us," or "our") collects, uses, and protects your personal information when you use our website https://thataihumanizer.pages.dev (the "Site") and related services.
              </p>
              <p className="text-muted-foreground leading-relaxed">
                By using our services, you agree to the practices outlined below.
              </p>
            </CardContent>
          </Card>

          {/* Main Sections */}
          {sections.map((section, sectionIndex) => (
            <Card key={section.id} id={section.id} className="mb-8 scroll-mt-8">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  {section.icon}
                  {sectionIndex + 1}. {section.title}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {section.content.map((item, itemIndex) => (
                  <div key={itemIndex} className="space-y-2">
                    <h4 className="font-semibold text-foreground">
                      {item.subtitle}
                    </h4>
                    <p className="text-muted-foreground leading-relaxed">
                      {item.text}
                    </p>
                  </div>
                ))}
              </CardContent>
            </Card>
          ))}

          {/* Contact Information */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5" />
                11. Contact Us
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <p className="text-muted-foreground leading-relaxed">
                  If you have any questions or requests regarding your privacy, please contact us at:
                </p>
                <div className="bg-muted/30 p-4 rounded-lg space-y-2">
                  <p className="text-sm">
                    <strong>📧 Email:</strong> thataihumanizer@gmail.com
                  </p>
                </div>
                <div className="flex gap-4 pt-4">
                  <Button variant="outline" asChild>
                    <Link to="/terms">Terms of Service</Link>
                  </Button>
                  <Button asChild>
                    <Link to="/contact">Contact Support</Link>
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Footer */}
      <div className="border-t border-border bg-muted/20 py-8">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-sm text-muted-foreground">
              This privacy policy is effective as of {lastUpdated} and applies to all users of ThatAIHumanizer services.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage; 